// The share artifact.
//
// The old share was `🔠 Jumble-Cross | 09/25/2026 ⏱️ 2:14 | Top 12%` — a flex.
// It told someone who had never heard of the game that you were fast at
// something, and gave them no reason to tap. Wordle's growth was the emoji
// grid, not the game.
//
// This sends the day's black-square pattern instead. It is spoiler-free (the
// shape is visible on the board before you solve anything), and measured
// unique across 120 consecutive days, so it reads as "today's puzzle" rather
// than as a generic logo.
//
// Sizing was measured rather than guessed. Seven emoji are 119pt against a
// 248-284pt iMessage bubble at default type, so the grid never wraps in normal
// use — it only breaks at Dynamic Type 40pt+, where Wordle's five are also
// close. Height is the real cost (49 emoji ≈ 29% of an iPhone screen), which
// is why the header is one line rather than two.

const FILLED = '🟦';
const BLOCKED = '⬛';
const ASSISTED = '🟨';

/** m:ss, with no leading zero on the minutes. */
export const formatShareTime = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * The grid as emoji, derived from which cells hold letters — never from the
 * letters themselves.
 *
 * @param {Array} grid           the played board
 * @param {Set}   assistedKeys   "r,c" of cells filled by an assist, marked in
 *                               a third colour so the help is visible
 */
export const buildEmojiGrid = (grid, assistedKeys = new Set()) =>
  grid
    .map((row, r) =>
      row
        .map((cell, c) => {
          if (cell.isBlocked) return BLOCKED;
          return assistedKeys.has(`${r},${c}`) ? ASSISTED : FILLED;
        })
        .join('')
    )
    .join('\n');

/**
 * Standing relative to the field.
 * Under ten players an ordinal is more honest than a percentage: "Top 50%" out
 * of two is technically true and plainly silly.
 */
export const buildStanding = (position, total) => {
  if (!position || !total || position === 'N/A') return null;
  if (total < 10) return `${position}/${total}`;
  return `Top ${Math.max(1, Math.round((position / total) * 100))}%`;
};

/**
 * Assemble the share.
 *
 * Deliberately one header line. Two lines wrapped raggedly in the bubble and
 * cost ~20pt of height for nothing.
 */
export const buildShareText = ({
  puzzleId,
  totalTime,
  position,
  totalPlayers,
  grid,
  assistedKeys,
  assistCount = 0,
  isArchive = false,
  url = 'https://www.jumble-cross.com',
}) => {
  const bits = [`🔠 #${puzzleId}`, `⏱️ ${formatShareTime(totalTime)}`];

  // An archive run is not ranked, so it never claims a standing.
  const standing = isArchive ? null : buildStanding(position, totalPlayers);
  if (standing) bits.push(standing);
  if (assistCount > 0) bits.push(`💡 ${assistCount}`);
  if (isArchive) bits.push('archive');

  return `${bits.join(' · ')}\n${buildEmojiGrid(grid, assistedKeys)}\n${url}`;
};

export default buildShareText;
